import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api/client';
import { useI18n } from '../i18n';
import { useSupport } from '../hooks/useSupport';
import { abrirPreferenciasDeCookies } from '../lib/consentimento';

/**
 * Rodapé do site.
 *
 * Fecha a página com o que o cliente procura quando rola até o fim: para onde
 * ir, como falar com alguém e quem está por trás da loja. As colunas seguem a
 * ordem da decisão — primeiro o que dá para fazer aqui, depois a conta, e só
 * então o contato, que é a saída para fora do site.
 *
 * O aviso sobre a Valve não é enfeite jurídico: uma loja de skins que não diz
 * que é independente levanta a dúvida no cliente justamente na hora de pagar.
 */

/** Perfil da loja. Trocar aqui muda o link e o texto ao mesmo tempo. */
const INSTAGRAM = 'dmsskins';

export function SiteFooter() {
  const { lang } = useI18n();
  const pt = lang === 'pt';
  const atendimento = useSupport();
  const [whatsapp, setWhatsapp] = useState<string | null>(null);

  /**
   * O número mora no painel e pode mudar; o rodapé lê o link pronto em vez de
   * guardar o telefone em código.
   *
   * Com uma tentativa de repescagem: a primeira chamada acontece no carregamento
   * da página, e qualquer soluço de rede ali sumiria com o contato até a pessoa
   * recarregar. Se ainda assim não vier, o item não some — vira o botão que
   * abre o atendimento, logo abaixo.
   */
  useEffect(() => {
    let vivo = true;
    let timer = 0;

    const buscar = (tentativa: number) => {
      void api
        .linkAtendimento()
        .then((r) => {
          if (!vivo) return;
          if (r.whatsapp) setWhatsapp(r.whatsapp);
          else if (tentativa === 0) timer = window.setTimeout(() => buscar(1), 2000);
        })
        .catch(() => {
          if (vivo && tentativa === 0) timer = window.setTimeout(() => buscar(1), 2000);
        });
    };

    buscar(0);
    return () => {
      vivo = false;
      window.clearTimeout(timer);
    };
  }, []);

  return (
    <footer className="rodape">
      <div className="rodape-inner">
        <div className="rodape-marca">
          <img
            className="rodape-logo"
            src="/logo.png"
            srcSet="/logo.png 1x, /logo-2x.png 2x"
            alt="DMS SKINS"
            width={168}
            height={57}
          />
          <p>
            {pt
              ? 'Skins de CS2 com preço final em reais e entrega no seu trade link. Sem cadastro de cartão e sem carteira: você fecha o pedido e fala com uma pessoa.'
              : 'CS2 skins with a final price in reais, delivered to your trade link. No stored cards, no wallet: you place the order and talk to a person.'}
          </p>
        </div>

        <nav className="rodape-col" aria-label={pt ? 'Serviços' : 'Services'}>
          <h3>{pt ? 'Loja' : 'Store'}</h3>
          <Link to="/all">{pt ? 'Skins disponíveis' : 'Available skins'}</Link>
          <Link to="/vender">{pt ? 'Venda de inventário' : 'Sell inventory'}</Link>
          <Link to="/upgrade">{pt ? 'Trocar skins' : 'Trade skins'}</Link>
        </nav>

        <nav className="rodape-col" aria-label={pt ? 'Sua conta' : 'Your account'}>
          <h3>{pt ? 'Sua conta' : 'Your account'}</h3>
          <Link to="/favoritos">{pt ? 'Favoritos' : 'Watchlist'}</Link>
          <Link to="/pedidos">{pt ? 'Meus pedidos' : 'My orders'}</Link>
          <Link to="/feedbacks">Feedbacks</Link>
          {/* Dúvidas não é uma página: abre a janela de atendimento, a mesma do
              botão do canto. */}
          <button type="button" onClick={atendimento.abrir}>
            {pt ? 'Dúvidas' : 'Help'}
          </button>
        </nav>

        <nav className="rodape-col contato" aria-label={pt ? 'Contato' : 'Contact'}>
          <h3>{pt ? 'Contato' : 'Contact'}</h3>
          <a
            href={`https://instagram.com/${INSTAGRAM}`}
            target="_blank"
            rel="noreferrer noopener"
            className="rodape-social"
          >
            <IconeInstagram />
            <span>@{INSTAGRAM}</span>
          </a>
          {/*
            O contato nunca desaparece. Com o link em mãos, vai direto para a
            conversa; sem ele — número ainda não cadastrado, ou a chamada que
            falhou — abre a janela de atendimento, que trata o caso e explica.
            Um item de contato que some por um soluço de rede é pior do que um
            caminho a mais.
          */}
          {whatsapp ? (
            <a href={whatsapp} target="_blank" rel="noreferrer noopener" className="rodape-social zap">
              <IconeWhatsapp />
              <span>{pt ? 'Falar no WhatsApp' : 'Chat on WhatsApp'}</span>
            </a>
          ) : (
            <button type="button" className="rodape-social zap" onClick={atendimento.abrir}>
              <IconeWhatsapp />
              <span>{pt ? 'Falar no WhatsApp' : 'Chat on WhatsApp'}</span>
            </button>
          )}
        </nav>
      </div>

      <div className="rodape-base">
        <p className="rodape-legal">
          {pt
            ? 'Projeto independente, sem vínculo com a Valve Corporation. Counter-Strike é marca registrada da Valve. Os valores exibidos são de referência e são confirmados no fechamento do pedido.'
            : 'Independent project, not affiliated with Valve Corporation. Counter-Strike is a trademark of Valve. Prices shown are for reference and are confirmed when the order is placed.'}
        </p>
        {/* O ano sai do relógio: rodapé com ano fixo envelhece sozinho e passa
            a impressão de site abandonado. */}
        {/* A LGPD pede que revogar seja tão fácil quanto aceitar: o banner
            volta por aqui, em qualquer página. */}
        <p className="rodape-privacidade">
          <Link to="/politica-de-privacidade">{pt ? 'Política de Privacidade' : 'Privacy Policy'}</Link>
          <span aria-hidden> · </span>
          <button type="button" onClick={abrirPreferenciasDeCookies}>
            {pt ? 'Preferências de cookies' : 'Cookie preferences'}
          </button>
        </p>
        {/* CNPJ no rodapé: é o que mostra que existe empresa por trás, na
            página inteira e não só na política de privacidade. */}
        <p className="rodape-copy">
          © {new Date().getFullYear()} <b>DMS</b> <span>SKINS</span>
          {' · '}
          <span className="rodape-cnpj">DMS SKINS LTDA · CNPJ 68.954.342/0001-84</span>
          {pt ? ' · Todos os direitos reservados.' : ' · All rights reserved.'}
        </p>
      </div>
    </footer>
  );
}

function IconeInstagram() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" aria-hidden focusable="false">
      <rect x="3" y="3" width="18" height="18" rx="5" />
      <circle cx="12" cy="12" r="4" />
      <circle cx="17.4" cy="6.6" r="1.1" fill="currentColor" stroke="none" />
    </svg>
  );
}

function IconeWhatsapp() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden focusable="false">
      <path
        fill="currentColor"
        d="M17.5 14.4c-.3-.2-1.7-.9-2-1-.3-.1-.5-.2-.7.1-.2.3-.7 1-.9 1.2-.2.2-.3.2-.6.1-.3-.2-1.2-.5-2.3-1.4-.9-.8-1.4-1.7-1.6-2-.2-.3 0-.5.1-.6l.5-.5c.1-.2.2-.3.3-.5 0-.2 0-.4-.1-.5l-.9-2.1c-.2-.5-.4-.5-.6-.5h-.6c-.2 0-.5.1-.8.4-.3.3-1 1-1 2.4s1 2.8 1.2 3c.2.2 2.1 3.2 5 4.4.7.3 1.2.5 1.7.6.7.2 1.3.2 1.8.1.6-.1 1.7-.7 1.9-1.4.2-.7.2-1.3.2-1.4-.1-.1-.3-.2-.6-.3z"
      />
      <path
        fill="none"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinejoin="round"
        d="M12 3.2a8.8 8.8 0 0 0-7.5 13.4L3.3 20.7l4.2-1.1A8.8 8.8 0 1 0 12 3.2z"
      />
    </svg>
  );
}
